import { useContext, useState, useEffect } from 'react';
import { Card, CardContent, Box, Typography, IconButton, Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import { TranslationContext } from '../../../Context/TranslationContext';
import { useTranslation } from '../../../hooks/useTranslation';
import { LOCALE_BY_LANG } from '../../../Utils/i18nHelpers';

export const WidgetWelcome = ({ onRemove, isEditMode }) => {
    const t = useTranslation('Dashboard.json');
    const { currentLanguage } = useContext(TranslationContext);
    const locale = LOCALE_BY_LANG[currentLanguage] || LOCALE_BY_LANG.fr;
    const [currentTime, setCurrentTime] = useState(new Date());

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);

        return () => clearInterval(timer);
    }, []);

    const handleRemove = (e) => {
        e.stopPropagation();
        e.preventDefault();
        onRemove();
    };

    const getGreeting = () => {
        const hour = currentTime.getHours();
        if (hour < 12) return t('widget.welcome.greeting.morning');
        if (hour < 18) return t('widget.welcome.greeting.afternoon');
        return t('widget.welcome.greeting.evening');
    };

    const getIcon = () => {
        const hour = currentTime.getHours();
        if (hour >= 6 && hour < 20) {
            return <WbSunnyIcon sx={{ fontSize: 48, color: '#FFD166' }} />;
        }
        return <NightsStayIcon sx={{ fontSize: 48, color: '#F4B183' }} />;
    };

    // Dégradés dérivés de l'orange de marque (#FF7233), un par moment de la journée
    const getGradient = () => {
        const hour = currentTime.getHours();
        if (hour >= 6 && hour < 12) {
            return 'linear-gradient(135deg, #FFB37A 0%, #FF7233 100%)';
        } else if (hour >= 12 && hour < 18) {
            return 'linear-gradient(135deg, #FF7233 0%, #E05A1A 100%)';
        } else if (hour >= 18 && hour < 20) {
            return 'linear-gradient(135deg, #E05A1A 0%, #B23A0F 100%)';
        }
        return 'linear-gradient(135deg, #3D2412 0%, #7A3B12 100%)';
    };

    const formatTime = () => {
        return currentTime.toLocaleTimeString(locale, {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    const formatDate = () => {
        return currentTime.toLocaleDateString(locale, {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    return (
        <Card
            elevation={0}
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '16px',
                background: getGradient(),
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
                }
            }}
        >
            <Box
                sx={{
                    p: 2,
                    cursor: 'move',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    '&:hover .widget-remove-btn': {
                        opacity: 1,
                        pointerEvents: 'all'
                    }
                }}
                className="widget-header"
            >
                <Chip
                    label={t('widget.welcome.title')}
                    size="small"
                    sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.25)',
                        color: 'white',
                        fontWeight: 600,
                        fontFamily: 'Poppins',
                        backdropFilter: 'blur(10px)'
                    }}
                />
                {isEditMode && (
                    <IconButton
                        className="widget-remove-btn"
                        onClick={handleRemove}
                        onMouseDown={(e) => e.stopPropagation()}
                        size="small"
                        sx={{
                            bgcolor: 'rgba(255, 255, 255, 0.2)',
                            color: 'white',
                            opacity: 0,
                            pointerEvents: 'none',
                            transition: 'all 0.2s ease',
                            backdropFilter: 'blur(10px)',
                            '&:hover': {
                                bgcolor: 'rgba(255, 255, 255, 0.3)'
                            }
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                )}
            </Box>
            <CardContent
                sx={{
                    flex: 1,
                    p: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    color: 'white'
                }}
            >
                <Box sx={{ mb: 2, opacity: 0.9 }}>
                    {getIcon()}
                </Box>
                <Typography
                    variant="h4"
                    sx={{
                        mb: 2,
                        fontSize: 'clamp(18px, 2.5vw, 24px)',
                        fontFamily: 'Poppins',
                        fontWeight: 500,
                        opacity: 0.95
                    }}
                >
                    {getGreeting()}
                </Typography>
                <Typography
                    variant="h1"
                    sx={{
                        fontSize: 'clamp(36px, 6vw, 56px)',
                        fontWeight: 700,
                        fontFamily: 'Poppins',
                        letterSpacing: '-0.02em',
                        mb: 1
                    }}
                >
                    {formatTime()}
                </Typography>
                <Typography
                    variant="body1"
                    sx={{
                        fontSize: 'clamp(13px, 1.5vw, 15px)',
                        fontFamily: 'Poppins',
                        opacity: 0.9,
                        textTransform: 'capitalize'
                    }}
                >
                    {formatDate()}
                </Typography>
            </CardContent>
        </Card>
    );
};
